import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, TextField, Button, FormControl, FormHelperText, Grid, 
  Alert, Paper, RadioGroup, FormControlLabel, Radio, FormLabel, Tooltip,
  IconButton, CircularProgress, Autocomplete
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createNewUser } from '../../../redux/slices/userSlice';
import { AppDispatch, RootState } from '../../../redux/store';
import { useAdminApi } from '../hooks/useAdminApi';
import { OrganizationListResponse } from '../types/adminApiTypes';

const CreateUserView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { error: userCreationError } = useSelector((state: RootState) => state.user);
  
  // Polling reference to check session storage during submission
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Format current date in DD.MM.YYYY format
  const formatDateString = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };
  
  const today = formatDateString(new Date());
  
  // Form state
  const [formIsValid, setFormIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResult, setFormResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  
  const [userData, setUserData] = useState({
    email: '',
    organization: '',
    user_type: 'standard' as string,
    // Date fields with current date as default
    created_at: today,
    subscription_start: today,
    subscription_end: '',
    // Default values for conversation counts
    message_count: 0,
    fraktal_conversation_count: 0,
    reflection_conversation_count: 0,
    fast_thinking_conversation_count: 0
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch existing organizations to power the org autocomplete field
  const { data: orgsData, loading: orgsLoading, fetchData: fetchOrgs } = useAdminApi<OrganizationListResponse>();
  const [availableOrgs, setAvailableOrgs] = useState<string[]>([]);

  useEffect(() => {
    fetchOrgs({ endpoint: '/organizations' });
  }, [fetchOrgs]);

  useEffect(() => {
    if (orgsData?.organizations) {
      const names = orgsData.organizations.map(o => o.organization).sort((a, b) => a.localeCompare(b));
      setAvailableOrgs(names);
    }
  }, [orgsData]);

  // Check for stored form result on component mount and re-check after delay
  useEffect(() => {
    const checkForStoredResult = () => {
      try {
        const storedResultString = sessionStorage.getItem('createUserResult');
        if (storedResultString) {
          const storedResult = JSON.parse(storedResultString);
          // Only update state if the stored result is different or indicates completion
          setFormResult(currentResult => {
            if (JSON.stringify(currentResult) !== JSON.stringify(storedResult)) {
              console.log('Restoring/Updating form result from sessionStorage:', storedResult);
              return storedResult;
            } 
            return currentResult; // No change needed
          });

          // Reset form fields only if it's a *final* success message
          if (storedResult.success && !storedResult.message.includes('Creating user')) {
            console.log('Found final success result, resetting form');
            setUserData({
              email: '',
              organization: '',
              user_type: 'standard',
              created_at: today,
              subscription_start: today,
              subscription_end: '',
              message_count: 0,
              fraktal_conversation_count: 0,
              reflection_conversation_count: 0,
              fast_thinking_conversation_count: 0
            });
          }
        } else {
          console.log('No stored form result found initially.');
        }
      } catch (error) {
        console.error('Error reading stored result:', error);
      }
    };

    // Check immediately on mount
    checkForStoredResult();

    // Re-check after a short delay to catch updates post-remount
    const recheckTimer = setTimeout(checkForStoredResult, 500); 

    return () => {
      clearTimeout(recheckTimer);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Polling logic
  useEffect(() => {
    if (isSubmitting) {
      console.log('Starting sessionStorage polling for result updates');
      if (pollingRef.current) clearInterval(pollingRef.current);

      pollingRef.current = setInterval(() => {
        try {
          const storedResultString = sessionStorage.getItem('createUserResult');
          if (storedResultString) {
            const storedResult = JSON.parse(storedResultString);
            // Update component state only if the stored result is different
            setFormResult(currentResult => {
              if (JSON.stringify(currentResult) !== JSON.stringify(storedResult)) {
                console.log('Polling: Detected change in sessionStorage, updating state:', storedResult);
                // Stop polling if we get a final success/error message
                if (!storedResult.message.includes('Creating user')) {
                  if (pollingRef.current) clearInterval(pollingRef.current);
                }
                return storedResult;
              }
              return currentResult;
            });
          }
        } catch (error) {
          console.error('Error during polling check:', error);
        }
      }, 500); // Check every 500ms
    } else {
      if (pollingRef.current) {
        console.log('Stopping polling.');
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isSubmitting]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when field is edited
      if (formErrors[name]) {
        setFormErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
      
      // Validate form after changes
      validateFormRealTime({
        ...userData,
        [name]: value
      });
    }
  };
  
  // Real-time validation to enable/disable submit button
  const validateFormRealTime = (data = userData) => {
    // Check required fields
    const emailValid = !!data.email && /\S+@\S+\.\S+/.test(data.email);
    const organizationValid = !!data.organization;
    
    // Date validation function
    const isValidDateFormat = (dateStr: string): boolean => {
      if (!dateStr) return true; // Empty is allowed
      
      // Check format DD.MM.YYYY
      const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
      const match = dateStr.match(regex);
      
      if (!match) return false;
      
      // Extract parts and check if it's a valid date
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Months are 0-indexed in JS
      const year = parseInt(match[3], 10);
      
      const date = new Date(year, month, day);
      return (
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      );
    };
    
    // Parse dates for comparison
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      
      const [day, month, year] = dateStr.split('.').map(Number);
      return new Date(year, month - 1, day);
    };
    
    // Check date formats if they're not empty
    const createdAtValid = isValidDateFormat(data.created_at);
    const subscriptionStartValid = isValidDateFormat(data.subscription_start);
    const subscriptionEndValid = isValidDateFormat(data.subscription_end);
    
    // Check subscription_end is after subscription_start if both are provided
    let datesSequenceValid = true;
    if (data.subscription_end && data.subscription_start && 
        subscriptionEndValid && subscriptionStartValid) {
      const startDate = parseDate(data.subscription_start);
      const endDate = parseDate(data.subscription_end);
      
      if (startDate && endDate && endDate <= startDate) {
        datesSequenceValid = false;
      }
    }
    
    const isValid = emailValid && 
                   organizationValid && 
                   createdAtValid && 
                   subscriptionStartValid && 
                   subscriptionEndValid &&
                   datesSequenceValid;
    
    setFormIsValid(isValid);
    return isValid;
  };

  // Validate form for submission
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!userData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!userData.organization) {
      errors.organization = 'Organization is required';
    }
    
    // Date validation function
    const isValidDateFormat = (dateStr: string): boolean => {
      if (!dateStr) return true; // Empty is allowed
      
      // Check format DD.MM.YYYY
      const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
      const match = dateStr.match(regex);
      
      if (!match) return false;
      
      // Extract parts and check if it's a valid date
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Months are 0-indexed in JS
      const year = parseInt(match[3], 10);
      
      const date = new Date(year, month, day);
      return (
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      );
    };
    
    // Parse dates for comparison
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      
      const [day, month, year] = dateStr.split('.').map(Number);
      return new Date(year, month - 1, day);
    };
    
    // Validate date fields if not empty
    if (userData.created_at && !isValidDateFormat(userData.created_at)) {
      errors.created_at = 'Use format DD.MM.YYYY';
    }
    
    if (userData.subscription_start && !isValidDateFormat(userData.subscription_start)) {
      errors.subscription_start = 'Use format DD.MM.YYYY';
    }
    
    if (userData.subscription_end && !isValidDateFormat(userData.subscription_end)) {
      errors.subscription_end = 'Use format DD.MM.YYYY';
    }
    
    // Check subscription_end is after subscription_start if both are provided
    if (userData.subscription_end && userData.subscription_start && 
        isValidDateFormat(userData.subscription_end) && 
        isValidDateFormat(userData.subscription_start)) {
      
      const startDate = parseDate(userData.subscription_start);
      const endDate = parseDate(userData.subscription_end);
      
      if (startDate && endDate && endDate <= startDate) {
        errors.subscription_end = 'End date must be later than start date';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Dedicated handler for the organization autocomplete field
  const handleOrganizationChange = (value: string) => {
    setUserData(prev => ({ ...prev, organization: value }));
    if (formErrors.organization) {
      setFormErrors(prev => ({ ...prev, organization: '' }));
    }
    validateFormRealTime({ ...userData, organization: value });
  };

  // Helper text for the org field: distinguishes new vs existing orgs
  const getOrgHelperText = (): string => {
    if (formErrors.organization) return formErrors.organization;
    const trimmed = userData.organization.trim();
    if (!trimmed) return 'Select an existing organization or type a new one';
    const isExisting = availableOrgs.some(org => org.toLowerCase() === trimmed.toLowerCase());
    if (!isExisting) return `"${trimmed}" will be created as a new organization`;
    return '';
  };

  // Format date to timestamp
  const formatDateToTimestamp = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    const [day, month, year] = dateStr.split('.').map(Number);
    return new Date(year, month - 1, day);
  };

  // Handle form submission
  const handleCreateUser = async () => {
    console.log("Creating user - validating form...");
    if (validateForm()) {
      setIsSubmitting(true);
      // Clear component state result, but not sessionStorage yet
      setFormResult(null); 

      try {
        // Parse dates
        const now = new Date();
        const created = userData.created_at ? formatDateToTimestamp(userData.created_at) : now;
        const subscriptionStart = userData.subscription_start ? formatDateToTimestamp(userData.subscription_start) : now;
        const subscriptionEnd = userData.subscription_end ? formatDateToTimestamp(userData.subscription_end) : null;
        
        // Cast to the expected type required by createNewUser thunk
        const submitData = {
          ...userData,
          user_type: userData.user_type as 'standard' | 'admin',
          created_at: created,
          subscription_start: subscriptionStart,
          subscription_end: subscriptionEnd
        };
        
        const emailBeingCreated = submitData.email;
        sessionStorage.setItem('lastCreatedEmail', emailBeingCreated);
        sessionStorage.setItem('createUserStartTime', new Date().getTime().toString());
        
        // Set initial "Creating..." message in UI and sessionStorage
        const inProgressResult = {
          success: true, // Treat as success for UI purposes (e.g., Alert severity)
          message: `User ${emailBeingCreated} created successfully! A password reset email has been sent.`
        };
        setFormResult(inProgressResult);
        sessionStorage.setItem('createUserResult', JSON.stringify(inProgressResult));
        
        // Attempt to create the user
        await dispatch(createNewUser(submitData)).unwrap();
        
        // Set final success message
        const finalResult = {
          success: true,
          message: `User ${emailBeingCreated} created successfully! A password reset email has been sent.`
        };
        // Update UI and sessionStorage
        setFormResult(finalResult);
        sessionStorage.setItem('createUserResult', JSON.stringify(finalResult));
        
        // Reset form fields for potential next creation
        setUserData({
          email: '',
          organization: '',
          user_type: 'standard',
          created_at: today,
          subscription_start: today,
          subscription_end: '',
          message_count: 0,
          fraktal_conversation_count: 0,
          reflection_conversation_count: 0,
          fast_thinking_conversation_count: 0
        });
        
      } catch (error) {
        console.error('Failed to create user:', error);
        const errorResult = {
          success: false,
          message: `Failed to create user: ${error instanceof Error ? error.message : String(error)}`
        };
        // Update UI and sessionStorage
        setFormResult(errorResult);
        sessionStorage.setItem('createUserResult', JSON.stringify(errorResult));
      } finally {
        setIsSubmitting(false); // This will stop the polling via the useEffect
      }
    }
  };

  // Get validation error message for tooltip
  const getValidationErrorMessage = (): string => {
    if (formIsValid) return '';
    
    // Check for specific errors
    if (!userData.email || !/\S+@\S+\.\S+/.test(userData.email)) {
      return 'Please enter a valid email address';
    }
    
    if (!userData.organization) {
      return 'Organization is required';
    }
    
    // Helper functions
    const isValidDateFormat = (dateStr: string): boolean => {
      if (!dateStr) return true;
      const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
      return regex.test(dateStr);
    };
    
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      const [day, month, year] = dateStr.split('.').map(Number);
      return new Date(year, month - 1, day);
    };
    
    // Check date formats
    if (userData.created_at && !isValidDateFormat(userData.created_at)) {
      return 'Created At date format should be DD.MM.YYYY';
    }
    
    if (userData.subscription_start && !isValidDateFormat(userData.subscription_start)) {
      return 'Subscription Start date format should be DD.MM.YYYY';
    }
    
    if (userData.subscription_end && !isValidDateFormat(userData.subscription_end)) {
      return 'Subscription End date format should be DD.MM.YYYY';
    }
    
    // Check subscription_end is after subscription_start
    if (userData.subscription_end && userData.subscription_start &&
        isValidDateFormat(userData.subscription_end) && 
        isValidDateFormat(userData.subscription_start)) {
      
      const startDate = parseDate(userData.subscription_start);
      const endDate = parseDate(userData.subscription_end);
      
      if (startDate && endDate && endDate <= startDate) {
        return 'Subscription End date must be later than Subscription Start date';
      }
    }
    
    return 'Please fill in all required fields with valid information';
  };

  // Run validation on initial load
  useEffect(() => {
    validateFormRealTime();
  }, []);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          component={RouterLink} 
          to="/dashboard/users" 
          aria-label="back to users"
          sx={{ mr: 2 }}
          onClick={() => {
            // Clear stored result when navigating away
            sessionStorage.removeItem('createUserResult');
            sessionStorage.removeItem('lastCreatedEmail');
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Create New User</Typography>
      </Box>
      
      <Paper sx={{ p: 4, position: 'relative' }}>
        {/* Loading overlay that only covers the form, not the whole screen */}
        {isSubmitting && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'primary.main',
              color: 'white',
              py: 0.5,
              px: 2,
              borderRadius: 4,
              boxShadow: 2
            }}
          >
            <CircularProgress 
              size={20} 
              color="inherit" 
              thickness={5}
            />
            <Typography variant="caption">Creating user...</Typography>
          </Box>
        )}
        
        {formResult && (
          <Alert 
            severity={formResult.success ? "success" : "error"} 
            sx={{ 
              mb: 3,
              position: 'sticky',
              top: 0,
              zIndex: 2
            }}
            action={
              formResult.success ? (
                <Button 
                  color="inherit" 
                  size="small"
                  onClick={() => {
                    // Clear stored result when navigating away
                    sessionStorage.removeItem('createUserResult');
                    sessionStorage.removeItem('lastCreatedEmail');
                    navigate('/dashboard/users');
                  }}
                >
                  Back to Users
                </Button>
              ) : (
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setFormResult(null);
                    sessionStorage.removeItem('createUserResult');
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }
          >
            {formResult.message}
          </Alert>
        )}
        
        {userCreationError && !formResult && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {userCreationError}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
              disabled={isSubmitting || formResult?.success}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={orgsLoading ? [] : availableOrgs}
              loading={orgsLoading}
              loadingText="Loading organizations…"
              inputValue={userData.organization}
              onInputChange={(_, newValue) => handleOrganizationChange(newValue)}
              disabled={isSubmitting || formResult?.success}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization"
                  required
                  error={!!formErrors.organization}
                  helperText={getOrgHelperText()}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {orgsLoading && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth required disabled={isSubmitting || formResult?.success}>
              <FormLabel>User Type</FormLabel>
              <RadioGroup
                name="user_type"
                value={userData.user_type}
                onChange={handleInputChange}
                row
              >
                <FormControlLabel value="standard" control={<Radio />} label="Standard" />
                <FormControlLabel value="admin" control={<Radio />} label="Admin" />
              </RadioGroup>
              <FormHelperText>User permissions level</FormHelperText>
            </FormControl>
          </Grid>
          
          {/* Date Fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Date Information (Format: DD.MM.YYYY)
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Created At"
              name="created_at"
              value={userData.created_at}
              onChange={handleInputChange}
              placeholder="DD.MM.YYYY"
              error={!!formErrors.created_at}
              helperText={formErrors.created_at || "Leave empty for current date"}
              disabled={isSubmitting || formResult?.success}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Subscription Start"
              name="subscription_start"
              value={userData.subscription_start}
              onChange={handleInputChange}
              placeholder="DD.MM.YYYY"
              error={!!formErrors.subscription_start}
              helperText={formErrors.subscription_start || "Leave empty for current date"}
              disabled={isSubmitting || formResult?.success}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Subscription End"
              name="subscription_end"
              value={userData.subscription_end}
              onChange={handleInputChange}
              placeholder="DD.MM.YYYY"
              error={!!formErrors.subscription_end}
              helperText={formErrors.subscription_end || "Can remain empty (unlimited)"}
              disabled={isSubmitting || formResult?.success}
            />
          </Grid>
          
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            {!formResult?.success ? (
              <>
                <Button 
                  variant="outlined" 
                  component={RouterLink} 
                  to="/dashboard/users"
                  disabled={isSubmitting}
                  onClick={() => {
                    // Clear stored result when navigating away
                    sessionStorage.removeItem('createUserResult');
                    sessionStorage.removeItem('lastCreatedEmail');
                  }}
                >
                  Cancel
                </Button>
                
                <Tooltip title={getValidationErrorMessage()}>
                  <span>
                    <Button 
                      onClick={handleCreateUser} 
                      variant="contained" 
                      color="primary"
                      disabled={isSubmitting || !formIsValid}
                    >
                      Create User
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setFormResult(null);
                    sessionStorage.removeItem('createUserResult');
                  }}
                >
                  Create Another User
                </Button>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  component={RouterLink} 
                  to="/dashboard/users"
                  onClick={() => {
                    // Clear stored result when navigating away
                    sessionStorage.removeItem('createUserResult');
                    sessionStorage.removeItem('lastCreatedEmail');
                  }}
                >
                  Back to Users
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CreateUserView; 